import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { buildField } from '../SystemSettings/common/utils';
import Button from '../SystemSettings/components/Controls/Button/Button';
import SelectField from '../SystemSettings/components/Controls/SelectField/SelectField';
import TableSelect from './TableSelect';
import styles from './BulkAssign.module.css';

const AZ_FIELD = {
    ID: 'selectedAz',
    NAME: 'Availability zone'
};

const BulkAssign = ({
    onCancel,
    onSave,
    azOptions,
    wallOptions,
    dcOptions,
    getDomainByAzGuid,
    getEntityIdsByAzGuid
}) => {
    const [selectedAz, setSelectedAz] = useState(azOptions[0].value);
    const [wallsToMove, setWallsToMove] = useState([]);
    const [dcsToMove, setDcsToMove] = useState([]);
    const [wallOptionsToDisplay, setWallOptionsToDisplay] = useState([]);
    const [dcOptionsToDisplay, setDcOptionsToDisplay] = useState([]);

    const azFiled = useMemo(() => {
        return buildField(AZ_FIELD, selectedAz);
    }, [selectedAz]);

    useEffect(() => {
        const filterDomains = getDomainByAzGuid(selectedAz);
        const azEntities = getEntityIdsByAzGuid(selectedAz);
        const newWallOptions = wallOptions.filter((wallOption) => {
            // Checks if wall and az domains are the same.
            const domainsNoDuplications = new Set([...wallOption.domains, ...filterDomains]);
            return (
                !azEntities.walls.includes(wallOption.id) &&
                wallOption.domains.length === domainsNoDuplications.size &&
                filterDomains.length === domainsNoDuplications.size
            );
        });
        const newDcOptions = dcOptions.filter((dcOption) => {
            return filterDomains.includes(dcOption.domain) && !azEntities.dcs.includes(dcOption.id);
        });
        setWallOptionsToDisplay(newWallOptions);
        setDcOptionsToDisplay(newDcOptions);
    }, [selectedAz, getDomainByAzGuid, wallOptions, dcOptions, getEntityIdsByAzGuid]);

    const onWallChanged = useCallback(
        (toAdd, option) => {
            if (toAdd) {
                setWallsToMove([...wallsToMove, option]);
            } else {
                setWallsToMove(wallsToMove.filter((wall) => wall.id !== option.id));
            }
        },
        [wallsToMove]
    );

    const onDcChanged = useCallback(
        (toAdd, option) => {
            if (toAdd) {
                setDcsToMove([...dcsToMove, option]);
            } else {
                setDcsToMove(dcsToMove.filter((dc) => dc.id !== option.id));
            }
        },
        [dcsToMove]
    );
    return (
        <Modal id="bulk-assign-modal" show backdrop="static">
            <Modal.Header>
                <div>
                    <div className={styles.header}>Bulk Assign</div>
                </div>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                <SelectField
                    field={azFiled}
                    options={azOptions}
                    multichoice={false}
                    creatable={false}
                    changed={(id, value) => setSelectedAz(value)}
                />
                <div style={{ fontWeight: '700' }}>
                    {wallOptionsToDisplay.length === 0 && dcOptionsToDisplay.length === 0
                        ? 'No entities can be assigned to this availability zone.'
                        : 'Choose entities to assign to this availability zone:'}
                </div>
                {wallOptionsToDisplay.length > 0 && (
                    <TableSelect
                        options={wallOptionsToDisplay}
                        changed={onWallChanged}
                        labelText={'Silverfort Nodes'}
                        chosenOptions={wallsToMove}
                    />
                )}
                {dcOptionsToDisplay.length > 0 && (
                    <TableSelect
                        options={dcOptionsToDisplay}
                        changed={onDcChanged}
                        labelText={'Domain Controllers'}
                        chosenOptions={dcsToMove}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button className={styles.cancelButton} onClick={() => onCancel()}>
                    Cancel
                </Button>
                <Button
                    className={styles.saveButton}
                    onClick={() => {
                        onSave(selectedAz, wallsToMove, dcsToMove);
                    }}>
                    Done
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkAssign;
